import Icon1 from "@assets/icons/Icon";
import BaseModal from "@components/modals/BaseModal";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import Button from "@components/ui/buttons/Button";
import DropDownSearch from "@components/ui/dropdown/DropDownSearch";
import TextField from "@components/ui/textfields/TextField";
import DatePicker from "@components/ui/selection/DatePicker";
import TimePicker from "@components/ui/selection/TimePicker";
import { useEffect, useMemo, useState } from "react";
import type { VehicleReservation, Vehicle } from "types/garage/vehicules";
import { hourToTimeStr, timeStrToHour } from "@utils/timeGrid";
import { useDataStore } from "@store/user_org";
import { vehiclesApi } from "@services/api/vehicles";
import { ordresApi } from "@services/api/ordres";
import { navigateTo } from "context/navigation.service";
import RestitutionChangeConfirmModal from "@components/modals/RestitutionChangeConfirmModal";

/** Convert ISO date string (YYYY-MM-DD) to unix seconds */
function isoToSeconds(iso: string): number | null {
  if (!iso) return null;
  const d = new Date(iso + "T12:00:00");
  return Math.floor(d.getTime() / 1000);
}

/** Convert ISO date (YYYY-MM-DD) + a decimal hour (e.g. 17.5) to unix seconds. */
function isoDateAtHourToSeconds(iso: string, hour: number): number | null {
  if (!iso) return null;
  const h = Math.max(0, Math.min(23, Math.floor(hour)));
  const m = Math.round((hour - Math.floor(hour)) * 60);
  const d = new Date(`${iso}T${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:00`);
  return Math.floor(d.getTime() / 1000);
}

/** Convert unix seconds to ISO date string (YYYY-MM-DD) */
function secondsToIso(seconds: number): string {
  const d = new Date(seconds * 1000);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

interface ReservationModalProps {
  reservation: VehicleReservation;
  vehicles: Vehicle[];
  onClose: () => void;
  onSave: (updated: Partial<VehicleReservation>) => void;
  onDelete?: (id: string) => void;
}

export default function ReservationModal({
  reservation,
  vehicles,
  onClose,
  onSave,
  onDelete,
}: ReservationModalProps) {
  const [form, setForm] = useState({ ...reservation });
  const orgId = useDataStore((s) => s.orgData?.id);

  const isMultiDay = form.startDate !== form.endDate;

  const update = <K extends keyof VehicleReservation>(key: K, value: VehicleReservation[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  // Only offer vehicles that are free during the reservation window. The
  // availability endpoint counts THIS reservation as occupying its own vehicle,
  // so we always add the currently-assigned vehicle back (it must stay
  // selectable). null = not loaded yet → fall back to all vehicles.
  const [availableIds, setAvailableIds] = useState<Set<string> | null>(null);
  useEffect(() => {
    if (!orgId || !form.startDate || !form.endDate) return;
    let alive = true;
    setAvailableIds(null);
    vehiclesApi
      .available({
        org_id: orgId,
        start_date: form.startDate,
        end_date: form.endDate,
        start_hour: form.startDate === form.endDate ? form.startHour ?? undefined : undefined,
        end_hour:
          form.startDate === form.endDate && form.startHour != null && form.durationHours != null
            ? form.startHour + form.durationHours
            : undefined,
      })
      .then((list) => { if (alive) setAvailableIds(new Set(list.map((v) => v.id))); })
      .catch(() => { if (alive) setAvailableIds(null); });
    return () => { alive = false; };
  }, [orgId, form.startDate, form.endDate, form.startHour, form.durationHours]);

  // Restitution-sync confirm: when the end date of a reservation linked to an
  // ordre changes, offer to move the ordre's restitution date and notify the
  // client — same flow as an ordre restitution change.
  const [restitutionConfirm, setRestitutionConfirm] =
    useState<{ oldSeconds?: number; newSeconds: number } | null>(null);
  const [saving, setSaving] = useState(false);

  const commitReservation = () => {
    onSave({
      clientName: form.clientName,
      vehicleId: form.vehicleId,
      startDate: form.startDate,
      endDate: form.endDate,
      startHour: form.startHour,
      durationHours: form.durationHours,
      returnHour: form.returnHour,
    });
    onClose();
  };

  const handleSave = async () => {
    const endDateChanged = form.endDate !== reservation.endDate;
    if (reservation.ordreId && endDateChanged) {
      // Restitution time-of-day = the reservation's return hour (multi-day) or
      // its computed single-day end, falling back to 17h.
      const hour =
        form.returnHour ??
        (form.startHour != null && form.durationHours != null
          ? form.startHour + form.durationHours
          : 17);
      const newSeconds = isoDateAtHourToSeconds(form.endDate, hour);
      if (newSeconds) {
        try {
          const ordre = await ordresApi.get(reservation.ordreId);
          setRestitutionConfirm({ oldSeconds: ordre.restitution?.seconds, newSeconds });
          return;
        } catch {
          // Ordre unreadable — fall through and just save the reservation.
        }
      }
    }
    commitReservation();
  };

  const handleRestitutionConfirm = async (comment: string, notify: boolean) => {
    if (!restitutionConfirm || !reservation.ordreId) {
      commitReservation();
      return;
    }
    setSaving(true);
    try {
      await ordresApi.update(reservation.ordreId, {
        restitution: { seconds: restitutionConfirm.newSeconds },
      });
      if (notify) {
        await ordresApi.notifyRestitutionChange(reservation.ordreId, {
          old_date_seconds: restitutionConfirm.oldSeconds,
          new_date_seconds: restitutionConfirm.newSeconds,
          comment: comment || undefined,
        });
      }
    } catch (e) {
      console.error("Error syncing restitution from reservation:", e);
    } finally {
      setSaving(false);
      setRestitutionConfirm(null);
    }
    commitReservation();
  };

  // Available vehicles for the window + always the current one; until the
  // availability lookup resolves, show all so the dropdown is never empty.
  const vehicleOptions = useMemo(() => {
    const list = availableIds == null
      ? vehicles
      : vehicles.filter((v) => availableIds.has(v.id) || v.id === form.vehicleId);
    return list.map((v) => ({ value: v.id, label: `${v.name} — ${v.plate}` }));
  }, [vehicles, availableIds, form.vehicleId]);

  // Compute end time string for single-day reservations
  const endTimeStr = !isMultiDay && form.startHour != null && form.durationHours != null
    ? hourToTimeStr(form.startHour + form.durationHours)
    : "";

  return (
    <BaseModal
      title="Modifier la réservation"
      startIcon={<Icon1 name="modify" size={20} />}
      onClose={onClose}
      width="max-w-3xl"
    >
      <div className="flex flex-col gap-3 p-4">
        {/* Client */}
        <TextField
          label="Client"
          value={form.clientName}
          onChange={(v) => update("clientName", v)}
        />

        {/* Vehicle — only those free during the reservation window (+ current) */}
        <DropDownSearch
          label="Véhicule"
          options={vehicleOptions}
          value={form.vehicleId}
          onSelect={(v) => update("vehicleId", v as string)}
        />
        <span className="text-h4 text-neutral-400 -mt-1">
          {availableIds == null
            ? "Recherche des véhicules disponibles…"
            : "Seuls les véhicules disponibles sur la période sont proposés."}
        </span>

        {/* Linked ordre — jump to it */}
        {reservation.ordreId && (
          <button
            onClick={() => { navigateTo({ route: `/ordre/${reservation.ordreId}` }); onClose(); }}
            className="flex flex-row items-center gap-2 self-start text-h3 font-medium text-[#5C6BFF] hover:underline"
          >
            <Icon1 name="side-ordres" size={16} color="#5C6BFF" />
            Voir l'ordre lié
          </button>
        )}

        {/* Dates */}
        <div className="flex flex-row gap-2">
          <div className="flex-1">
            <DatePicker
              label="Date début"
              seconds={isoToSeconds(form.startDate)}
              onChange={(s) => update("startDate", secondsToIso(s))}
            />
          </div>
          <div className="flex-1">
            <DatePicker
              label="Date fin"
              seconds={isoToSeconds(form.endDate)}
              onChange={(s) => update("endDate", secondsToIso(s))}
              minDateSeconds={isoToSeconds(form.startDate)}
            />
          </div>
        </div>

        {/* Hours */}
        <div className="flex flex-row gap-2">
          <div className="flex-1">
            <TimePicker
              label="Heure de départ"
              value={form.startHour != null ? hourToTimeStr(form.startHour) : null}
              onChange={(timeStr) => {
                if (!timeStr) return;
                const newStart = timeStrToHour(timeStr);
                // For single-day, keep end hour consistent by adjusting duration
                if (!isMultiDay && form.startHour != null && form.durationHours != null) {
                  const endHour = form.startHour + form.durationHours;
                  update("durationHours", Math.max(0.25, endHour - newStart));
                }
                update("startHour", newStart);
              }}
            />
          </div>
          <div className="flex-1">
            <TimePicker
              label="Heure de fin"
              value={
                isMultiDay
                  ? (form.returnHour != null ? hourToTimeStr(form.returnHour) : null)
                  : (endTimeStr || null)
              }
              onChange={(timeStr) => {
                if (!timeStr) return;
                const endHour = timeStrToHour(timeStr);
                if (isMultiDay) {
                  update("returnHour", endHour);
                } else {
                  const start = form.startHour ?? 8;
                  update("durationHours", Math.max(0.25, endHour - start));
                }
              }}
            />
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="border-t-1 p-4 flex flex-row items-center justify-between gap-2">
        {onDelete ? (
          <button
            onClick={() => {
              if (window.confirm("Supprimer cette réservation ? Le véhicule sera libéré.")) {
                onDelete(reservation.id);
                onClose();
              }
            }}
            className="flex flex-row items-center gap-1.5 text-h3 font-medium text-red-500 hover:text-red-600"
          >
            <Icon1 name="delete" size={16} color="currentColor" />
            Supprimer
          </button>
        ) : <span />}
        <div className="flex flex-row gap-2">
          <SecondaryButton variant="small" onClick={onClose}>
            Fermer
          </SecondaryButton>
          <Button
            variant="small"
            label="Enregistrer"
            onClick={handleSave}
          />
        </div>
      </div>

      {restitutionConfirm && (
        <RestitutionChangeConfirmModal
          kind="restitution"
          oldSeconds={restitutionConfirm.oldSeconds}
          newSeconds={restitutionConfirm.newSeconds}
          saving={saving}
          onClose={() => setRestitutionConfirm(null)}
          onConfirm={handleRestitutionConfirm}
        />
      )}
    </BaseModal>
  );
}
